import { defineStore } from 'pinia'
import { LANGUAGES } from '~/constants/language'
import { logFetchWarning } from '~/utils/fetchErrorLog'

interface LanguageUpdatePayload {
  payload: Language[]
  main: Language
}

export const useLanguageStore = defineStore('language', () => {
  const value = ref<Language[]>([])
  const main = ref<Language>({
    name: '',
    display: '',
    code: 'en',
    show: false,
    default: false,
  })
  const current = ref<SupportedLocale>('en')
  const currentCurrencyId = ref<string | undefined>()

  const { $api, $i18n } = useNuxtApp()
  const route = useRoute()

  /**
   * URL path is the authoritative locale source — safe on SSR first load before any cookie is set.
   * Falls back to `current` (set after fetch) or the hardcoded default 'en'.
   */
  const resolvedLocale = computed<SupportedLocale>(() => {
    const [, pathLocale] = route.path.split('/')
    if ((LANGUAGES as readonly string[]).includes(pathLocale)) {
      return pathLocale as SupportedLocale
    }
    return current.value
  })

  /**
   * Check if the given locale code exists in available languages
   */
  const isValidLocale = (localeCode: string): localeCode is SupportedLocale => {
    return value.value.some(lang => lang.code === localeCode)
  }

  /**
   * Initialize language settings based on the given locale
   */
  const initializeLanguage = async (locale: SupportedLocale, mainLanguage: Language) => {
    await $i18n.setLocale(locale)
    $i18n.setLocaleCookie(locale)
    setCurrentLanguage(locale)
    main.value = mainLanguage
  }

  /**
   * Fetch available languages from API
   */
  const fetch = async () => {
    try {
      const response = await $api<ApiSuccessResponse<Language[]>>('/languages')
      const data = response.data
      const mainLang = data?.find((lang: Language) => lang.default)

      if (mainLang && data) {
        setCurrentCurrencyId(mainLang.currencyId)
        await update({ payload: data, main: mainLang })
      }
    } catch (error) {
      logFetchWarning('Failed to fetch languages', error)
      throw error
    }
  }

  /**
   * Update language store with new data
   */
  const update = async ({ payload, main: mainLang }: LanguageUpdatePayload) => {
    value.value = payload

    // Set current language if not set
    if (!current.value) {
      current.value = mainLang.code
    }

    // Set current currency symbol if not set
    if (!currentCurrencyId.value) {
      setCurrentCurrencyId(mainLang.currencyId)
    }

    // Check URL path for language code
    const [, pathLocale] = route.path.split('/')

    if (pathLocale && isValidLocale(pathLocale)) {
      await initializeLanguage(pathLocale, mainLang)
    } else {
      await initializeLanguage(mainLang.code, mainLang)
    }
  }

  const setCurrentLanguage = (code: SupportedLocale) => {
    current.value = code
  }

  const setCurrentCurrencyId = (currencyId: string | undefined) => {
    currentCurrencyId.value = currencyId
  }

  return {
    value,
    main,
    current,
    currentCurrencyId,
    resolvedLocale,
    fetch,
    update,
    setCurrentLanguage,
    setCurrentCurrencyId,
  }
})
